export const EXPORT_WAITING_MESSAGE = 'Export requested — awaiting progress update.';

export const EXPORT_COMMODITIES = [
	{ value: 'steel', label: 'Steel' },
	{ value: 'cu', label: 'Copper' },
	{ value: 'ni', label: 'Nickel' },
	{ value: 'al', label: 'Aluminium' }
];

export const AGGREGATE_DATASETS = {
	capacity: 'Capacity',
	index: 'Indices',
	productionSites: 'Site production',
	productionSubsites: 'Subsite production',
	co2Emissions: 'CO₂ emissions'
};

export const STEEL_EXPORT_PERIODS = ['weekly', 'monthly', 'annual'];

export const getExportRows = (response, commodity, exportType) => {
	if (exportType === 'site') {
		return [{ key: 'sites', label: 'Site data', status: response?.validation || null }];
	}

	const keys = commodity === 'steel'
		? Object.keys(AGGREGATE_DATASETS)
		: ['capacity', 'index'];
	const rows = keys.map((key) => ({
		key,
		label: AGGREGATE_DATASETS[key],
		status: response?.status?.[key] || null
	}));
	if (commodity === 'steel') {
		STEEL_EXPORT_PERIODS.forEach((period) => {
			['productionSites', 'productionSubsites', 'co2Emissions'].forEach((key) => {
				rows.push({
					key: `${key}:${period}`,
					label: AGGREGATE_DATASETS[key],
					status: response?.periods?.[period]?.[key] || null
				});
			});
		});
	}
	return rows;
};

// The start endpoint has no run ID. Only show results newer than the status
// captured before the request; an unchanged FINISHED result belongs to the past.
export const getCurrentExportRows = (rows, pending, exportType) => {
	if (!pending) return rows;
	return rows.map((row) => {
		const status = row.status;
		const previous = pending.baseline?.[row.key];
		let current = false;
		if (status && exportType === 'site') {
			current = status.state === 'RUNNING' ||
				Date.parse(status.validatedAt) >
					(Date.parse(previous?.validatedAt) || pending.requestedAt);
		} else if (status) {
			const startedAt = status.executionStartedAt || status.exportStartedAt;
			const previousStartedAt = previous?.executionStartedAt || previous?.exportStartedAt;
			current = Number.isFinite(Date.parse(startedAt)) &&
				(previousStartedAt
					? Date.parse(startedAt) > Date.parse(previousStartedAt)
					: Date.parse(startedAt) >= pending.requestedAt);
		}
		return { ...row, status: current ? status : null };
	});
};

export const getExportState = (rows, pending = false) => {
	const statuses = rows.map(({ status }) => status);
	if (statuses.some((status) => status?.state === 'RUNNING')) return 'RUNNING';
	if (statuses.some((status) => status?.state === 'FAILED')) return 'FAILED';
	if (statuses.some((status) => !status || status.state === 'NOT_STARTED')) {
		return pending ? 'WAITING' : 'UNAVAILABLE';
	}
	if (statuses.some((status) => status.state === 'PARTIAL')) return 'PARTIAL';
	if (statuses.every((status) => status.state === 'FINISHED' &&
		(status.expected == null || status.completed === status.expected))) {
		return 'FINISHED';
	}
	return 'UNAVAILABLE';
};

export const isExportSettled = (rows) => rows.every(({ status }) =>
	status && ['FINISHED', 'PARTIAL', 'FAILED'].includes(status.state) &&
	(status.state !== 'FINISHED' || status.expected == null || status.completed === status.expected)
);

// Keep one row per dataset in the UI, including all outputs of a full steel
// export in its progress and outcome without exposing period selection.
export const getDisplayExportRows = (rows, pending) => {
	const groups = {};
	rows.forEach((row) => {
		const key = row.key.split(':')[0];
		if (!groups[key]) groups[key] = [];
		groups[key].push(row);
	});
	return Object.entries(groups).map(([key, group]) => {
		if (group.length === 1) return group[0];
		const statuses = group.map(({ status }) => status).filter(Boolean);
		if (!statuses.length) return { key, label: group[0].label, status: null };
		const state = getExportState(group, pending);
		const dates = statuses.map((status) => status.finishedAt).filter(Boolean).sort();
		return {
			key,
			label: group[0].label,
			status: {
				state,
				completed: statuses.reduce((sum, status) => sum + (status.completed || 0), 0),
				expected: statuses.length === group.length && statuses.every((status) => status.expected != null)
					? statuses.reduce((sum, status) => sum + status.expected, 0) : null,
				executionStartedAt: statuses[0].executionStartedAt,
				maxDate: group[0].status?.maxDate,
				finishedAt: state === 'FINISHED' ? dates[dates.length - 1] : null,
				message: [...new Set(statuses.map((status) => status.message).filter(Boolean))].join(' ')
			}
		};
	});
};
